import { useEffect, useState } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import PageSmall from '../../Components/Page/PageSmall';
import BookyBox from '../../Components/Layout/BookyBox';
import { TextService } from '../../Services/TextService';
import { getCurrentLanguage } from '../../Helpers/RouteHelper';
import CreateUserForm from '../../Components/CreateUser/CreateUser';
import { CompanyService } from '../../Services/CompanyService';
import NotFound from '../../Components/Page/NotFoundPage';
import { useAuth } from '../../Contexts/AuthContext';
import { Button, Typography } from 'antd';

const { Title, Paragraph } = Typography;

interface CompanyInvite {
    id: number;
    inviteEmail: string;
    inviteId: string;
    userAlreadyExists: boolean;
    companyName: string;
}

const CreateCompanyUser = () => {
    const navigation = useNavigate();
    let params = useParams();
    const companyInviteId = params.companyInviteId;
    const [errorText, setErrorText] = useState("" as string);
    const [invite, setInvite] = useState<CompanyInvite | null>(null);
    const [inviteFound, setInviteFound] = useState(true as boolean);
    const [accepting, setAccepting] = useState(false);
    const { user, loading } = useAuth();

    const GetText = TextService.GetText;

    const onCreateUserSubmit = async (formValues: any) => {
        let newUser: any = await CompanyService.ConnectUserToCompany({
            inviteId: companyInviteId,
            firstName: formValues.firstname,
            lastName: formValues.lastname,
            password: formValues.password
        });
        if (newUser && newUser.message != null) {
            setErrorText(newUser.message);
        } else {
            navigation("/" + getCurrentLanguage() + "/NewUserCreatedWithProvider");
        }
    }

    const onAcceptInvite = async () => {
        setAccepting(true);
        try {
            await CompanyService.AcceptInvite(companyInviteId!);
            navigation("/" + getCurrentLanguage() + "/users");
        } catch {
            setErrorText(GetText("CreateCompanyUser_AcceptFailed"));
        } finally {
            setAccepting(false);
        }
    };

    useEffect(() => {
        if (companyInviteId != null) {
            CompanyService.GetCompanyUserInvite(companyInviteId).then((result: CompanyInvite) => {
                if (result) {
                    setInvite(result);
                    setInviteFound(true);
                } else {
                    setInviteFound(false);
                }
            }).catch(() => {
                setInviteFound(false);
            });
        }
    }, [params]);

    const onRedirectToGoogle = () => {
        window.location.href = process.env.REACT_APP_API_URL + "/ExternalUser/signUpWithGoogle?language=" + getCurrentLanguage() + "&inviteId=" + companyInviteId;
    };

    if (loading) return null;

    if (!inviteFound) {
        return <PageSmall>
            <BookyBox>
                <NotFound message={GetText("CreateCompanyUser_InviteNotFound")} />
            </BookyBox>
        </PageSmall>;
    }

    const emailMatches = user && invite && user.email.toLowerCase() === invite.inviteEmail.toLowerCase();

    // Case 1: Logged in with the correct account — show accept button
    if (emailMatches) {
        return <PageSmall>
            <BookyBox>
                <Title level={3}>{GetText("CreateCompanyUser_LoggedIn_Title")}</Title>
                <Paragraph>{GetText("CreateCompanyUser_LoggedIn_Description").replace("{company}", invite!.companyName)}</Paragraph>
                {errorText && <Paragraph type="danger">{errorText}</Paragraph>}
                <Button type="primary" loading={accepting} onClick={onAcceptInvite}>
                    {GetText("CreateCompanyUser_LoggedIn_AcceptButton")}
                </Button>
            </BookyBox>
        </PageSmall>;
    }

    // Case 1b: Logged in with the wrong account
    if (user && invite && !emailMatches) {
        return <PageSmall>
            <BookyBox>
                <Title level={3}>{GetText("CreateCompanyUser_WrongAccount_Title")}</Title>
                <Paragraph>{GetText("CreateCompanyUser_WrongAccount_Description").replace("{inviteEmail}", invite.inviteEmail).replace("{currentEmail}", user.email)}</Paragraph>
            </BookyBox>
        </PageSmall>;
    }

    // Case 2: Not logged in but user already has an account
    if (!user && invite?.userAlreadyExists) {
        const loginUrl = "/" + getCurrentLanguage() + "/login?redirect=/" + getCurrentLanguage() + "/CreateCompanyUser/" + companyInviteId;
        return <PageSmall>
            <BookyBox>
                <Title level={3}>{GetText("CreateCompanyUser_ExistingAccount_Title")}</Title>
                <Paragraph>{GetText("CreateCompanyUser_ExistingAccount_Description")}</Paragraph>
                <Button type="primary" onClick={() => navigation(loginUrl)}>
                    {GetText("CreateCompanyUser_ExistingAccount_LoginButton")}
                </Button>
            </BookyBox>
        </PageSmall>;
    }

    // Case 3: New user — show registration form
    return <PageSmall>
        <BookyBox>
            <CreateUserForm
                finishSetupText={GetText("CreateCompanyUser_Info")}
                hideEmailInput={true}
                onRedirectToGoogle={onRedirectToGoogle}
                errorText={errorText}
                onCreateUserSubmit={onCreateUserSubmit}
            />
        </BookyBox>
    </PageSmall>;
}

export default CreateCompanyUser;
